import type { Variant } from '@trippath/shared';

/**
 * Where in the plan a variant goes: from the stop it leaves at to the stop it joins again at, or
 * to the end; one from elsewhere goes on after the end.
 */
function span(stops: readonly { id: string }[], variant: Variant): [number, number] {
  const at = (id: string | undefined) => stops.findIndex((s) => s.id === id);
  const start = variant.from === null ? stops.length : at(variant.from.id);
  const end = variant.rejoin === null ? stops.length : at(variant.rejoin.id);
  return [start, end];
}

/** Whether two variants change the same stretch of the plan, and so cannot both be taken. */
export function clash(stops: readonly { id: string }[], a: Variant, b: Variant): boolean {
  const [aStart, aEnd] = span(stops, a);
  const [bStart, bEnd] = span(stops, b);
  return aStart === bStart || Math.max(aStart, bStart) < Math.min(aEnd, bEnd);
}

/**
 * The variants shown after one is pressed: shown, it is put away; else it is shown beside those
 * that leave the plan elsewhere, and in place of those that change the same stretch.
 */
export function togglePick(
  stops: readonly { id: string }[],
  variants: readonly Variant[],
  picked: readonly string[],
  id: string,
): string[] {
  if (picked.includes(id)) return picked.filter((other) => other !== id);
  const pressed = variants.find((v) => v.id === id);
  if (pressed === undefined) return [...picked];
  const kept = picked.filter((other) => {
    const variant = variants.find((v) => v.id === other);
    return variant !== undefined && !clash(stops, variant, pressed);
  });
  return [...kept, id];
}
